import React from 'react'
import {
  View,
  ViewStyle,
  TextStyle,
  Image,
  useWindowDimensions,
  ImageStyle,
  Modal,
} from 'react-native'
import { observer } from 'mobx-react-lite'
import { color, Text, Button, Medal, Medallion } from '@trivie/ui-mobile'
import { useStores } from '@trivie/core'
import RenderHtml, { defaultSystemFonts } from 'react-native-render-html'
import { ReactComponent as LevelUpIcon } from './levelUpIcon.svg'
import { useMediaQuery } from '@mui/material'
import { Confetti } from '@trivie/ui-mobile/components/confetti'

export interface NotificationCheckScreenProps {}

export const NotificationCheckScreen = observer((props) => {
  const { userStore } = useStores()
  const { splashNotifications, splashNotification, setSplashNotifications } = userStore

  const { width } = useWindowDimensions()
  const systemFonts = [...defaultSystemFonts, 'Inter']

  const textColor =
    splashNotification?.notification_type === 'certification' ||
    splashNotification?.notification_type === 'medal'
      ? color.white
      : color.black
  const backgroundColor =
    splashNotification?.notification_type === 'certification' ||
    splashNotification?.notification_type === 'medal'
      ? color.quizBackground
      : color.white

  const handleContinue = () => {
    if (splashNotifications) {
      setSplashNotifications([...splashNotifications].slice(1) as any)
    }
  }

  const renderContent = () => {
    if (!splashNotification) {
      return null
    }
    switch (splashNotification.notification_type) {
      case 'badge':
        return (
          <>
            {!splashNotification?.locked && (
              <>
                <Text
                  align="center"
                  tx="Notifications.Badges.badge_earned"
                  style={{ color: textColor, marginBottom: 40 }}
                  variant="headline"
                />
              </>
            )}
            {splashNotification?.badge_icon_url && (
              <Image
                source={{ uri: splashNotification?.badge_icon_url }}
                style={{ ...BADGE_IMG, opacity: splashNotification?.locked ? 0.3 : 1.0 }}
              />
            )}
            <View style={{ marginTop: 40, flexDirection: 'column', alignItems: 'center' }}>
              {splashNotification.label && (
                <Text
                  text={splashNotification.label}
                  style={{ marginBottom: 8, color: textColor }}
                  variant="headline"
                />
              )}
              <Text
                style={{ fontSize: 17, color: textColor }}
                text={splashNotification?.description}
              />
            </View>
          </>
        )
      case 'certification':
        return (
          <>
            <View style={{ top: -80 }}>
              {splashNotification?.level && (
                <Medallion type={splashNotification?.level} recall={0} scale={2} />
              )}
            </View>
            <View style={{ flexDirection: 'column', alignItems: 'center' }}>
              <Text
                tx="Certifications.certification"
                uppercase
                style={{ marginTop: 20, marginBottom: 10, color: '#ffffff50' }}
                variant="titleEmphasized"
              />
              <Text
                variant="headline"
                style={{ color: textColor, marginBottom: 10 }}
                text={`${splashNotification?.level}`}
              />
              <Text
                style={{ marginBottom: 32, textAlign: 'center', color: textColor }}
                variant="labelMedium"
                tx="Certifications.certified"
                txOptions={{ level: splashNotification?.level, title: splashNotification?.label }}
              />
              <Text
                style={{ textAlign: 'center', color: textColor }}
                variant="labelMedium"
                tx="Certifications.tagline"
              />
            </View>
          </>
        )
      case 'user_level_up':
        return (
          <>
            <LevelUpIcon
              style={{
                alignSelf: 'center',
                width: 200,
                height: 200,
                borderRadius: 200,
                marginBottom: 40,
              }}
            />
            <Text
              variant="headline"
              tx="Notifications.UserLevel.leveled_up"
              style={{ textAlign: 'center', marginBottom: 20 }}
            />
            <Text
              style={{ textAlign: 'center' }}
              variant="labelEmphasized"
              text={splashNotification.label}
            />
          </>
        )
      case 'medal':
        return (
          <>
            <Medal
              style={{ alignSelf: 'center', marginBottom: 20 }}
              hideRecall
              level={splashNotification?.medal_level}
              recall={0}
              size={100}
            />
            <Text
              variant="headline"
              style={{ color: textColor, textAlign: 'center', marginBottom: 8 }}
              tx={`Quiz.${splashNotification?.medal_level?.toLowerCase()}Medal`}
            />
            <RenderHtml
              systemFonts={systemFonts}
              contentWidth={width}
              baseStyle={{ ...HTML, color: textColor }}
              source={{ html: splashNotification?.notification_text ?? '' }}
            />
            {splashNotification?.highest_level !== splashNotification?.medal_level && (
              <Text
                style={{ textAlign: 'center', color: textColor }}
                variant="titleEmphasized"
                txOptions={{
                  highestThreshold: splashNotification?.highest_threshold?.toFixed(0),
                  highestLevel: splashNotification?.highest_level,
                }}
                tx="Medals.maxEarnedGoal"
              />
            )}
          </>
        )
    }
  }

  const isMobile = !useMediaQuery('(min-width:800px)')

  return (
    <Modal
      visible={Boolean(splashNotifications?.length)}
      presentationStyle={isMobile ? 'fullScreen' : 'formSheet'}
      transparent
    >
      {!splashNotification?.hide_celebration && (
        <Confetti origin={{ x: window.innerWidth / 2, y: 0 }} />
      )}
      <View style={CONTAINER}>
        <View
          style={{
            height: isMobile ? '100%' : 'auto',
            width: isMobile ? '100%' : 500,
            alignSelf: 'center',
            borderRadius: isMobile ? 0 : 20,
            backgroundColor,
            position: 'relative',
          }}
        >
          <View style={CONTENT}>{renderContent()}</View>
          <View style={{ ...FOOTER, backgroundColor, padding: 20 }}>
            <Button
              textStyle={CONTINUE_BTN_TXT}
              tx={splashNotifications?.length > 1 ? 'Common.continue' : 'Common.close'}
              onPress={handleContinue}
            />
          </View>
        </View>
      </View>
    </Modal>
  )
})

/**
 * Styling
 */
const CONTENT: ViewStyle = {
  flex: 1,
  justifyContent: 'center',
  padding: 32,
  position: 'relative',
}
const CONTAINER: ViewStyle = {
  height: '100%',
  justifyContent: 'center',
  backgroundColor: 'rgba(0,0,0,0.6)',
}
const FOOTER: ViewStyle = {
  shadowColor: 'rgba(0,0,0,0.1)',
  shadowOffset: { width: 0, height: -4 },
  shadowOpacity: 0.4,
  shadowRadius: 4,
  elevation: 15,
  flexDirection: 'column',
  justifyContent: 'center',
  padding: 20,
  borderBottomRightRadius: 20,
  borderBottomLeftRadius: 20,
}
const CONTINUE_BTN_TXT: TextStyle = {
  fontSize: 17,
  lineHeight: 22,
  fontWeight: '600',
  fontFamily: 'Inter-SemiBold',
  color: color.white,
}
const BADGE_IMG: ImageStyle = {
  alignSelf: 'center',
  width: 280,
  height: 280,
  borderRadius: 280,
}
const HTML: any = { textAlign: 'center', marginBottom: 20, fontSize: 17 }
